import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { find } from 'lodash-es';
import slFeatureServiceModule from '@shopline/sl-feature/js/sl-feature';
import { SIGNUP_METHOD } from '../../../constants/userSetting';
import useGlobalSDK from '../../../hooks/useGlobalSDK';
import { parseUserSetting } from '../../../utils/userSetting';
import { userSettingPropTypes } from '../utils/propTypes';
import benchatService from '../../../service/benchatService';
import trackerService from '../../../service/trackerService';
import abTestService from '../../../service/abTestService';
import { getCookie, setCookie } from '../../../utils/cookie';
import { hasFeature } from '../../../utils/featureService';
import { THIRDPARTY_SIGNUP_LOGIN } from '../constants/abTestConst';

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export const AppContextProvider = ({
  children,
  defaultPanel,
  featuresLimit,
  userSetting: serverUserSetting,
  isFacebookLoginEnabled,
  isGoogleSignUpLoginEnabled,
  isLineLoginEnabled,
  defaultMembershipPromoBanner,
  ruleManagerExternalHost,
  ...restProps
}) => {
  const grecaptcha = useGlobalSDK('grecaptcha');

  const [currentPanel, setCurrentPanel] = useState(defaultPanel); // 'LOGIN' | 'SIGN_UP'
  const [defaultAccount, setDefaultAccount] = useState({
    email: '',
    mobile_phone: '',
  });
  const [variant, setVariant] = useState(() =>
    getCookie(THIRDPARTY_SIGNUP_LOGIN),
  );
  const [isVariantLoading, setIsVariantLoading] = useState(false);

  const userSetting = parseUserSetting(serverUserSetting);

  const isEmailRequired = [
    SIGNUP_METHOD.EMAIL,
    SIGNUP_METHOD.EMAIL_AND_MOBILE,
  ].includes(userSetting?.signup_method);

  const isMobilePhoneRequired = [
    SIGNUP_METHOD.MOBILE,
    SIGNUP_METHOD.EMAIL_AND_MOBILE,
  ].includes(userSetting?.signup_method);

  const smsVerificationEnabled =
    isMobilePhoneRequired && userSetting?.sms_verification?.status;

  const emailVerificationEnabled =
    isEmailRequired &&
    userSetting?.email_verification?.status &&
    userSetting?.login_with_verification?.status;

  const birthdaySetting =
    find(userSetting?.general_fields, {
      type: 'birthday',
    })?.options?.signup ?? {};
  const genderSetting =
    find(userSetting?.general_fields, {
      type: 'gender',
    })?.options?.signup ?? {};

  const isSocialLoginEnabled =
    isFacebookLoginEnabled || isLineLoginEnabled || isGoogleSignUpLoginEnabled;
  const slFeatureService = slFeatureServiceModule(featuresLimit);
  const hasThirdpartyLoginUIRevamp = hasFeature('thirdparty_login_ui_revamp');
  const isVariantUI = variant === 'variation';

  useEffect(() => {
    const fetchVariation = async () => {
      const distinct_id = getCookie('_spt');
      setIsVariantLoading(true);
      const res = await abTestService.getVariation(
        {
          name: THIRDPARTY_SIGNUP_LOGIN,
          distinct_id,
        },
        ruleManagerExternalHost,
      );
      setCookie(THIRDPARTY_SIGNUP_LOGIN, res?.data?.data?.group);
      setVariant(res?.data?.data?.group);
      setIsVariantLoading(false);
    };
    if (hasThirdpartyLoginUIRevamp && !variant) fetchVariation();
  }, [hasThirdpartyLoginUIRevamp, ruleManagerExternalHost, variant]);

  useEffect(() => {
    if (hasThirdpartyLoginUIRevamp && variant) {
      trackerService.experimentStart(
        {
          experiment_name: THIRDPARTY_SIGNUP_LOGIN,
          variation: variant,
        },
        'Member',
      );
    }
  }, [hasThirdpartyLoginUIRevamp, variant]);

  const contextProps = useMemo(
    () => ({
      userSetting,
      currentPanel,
      setCurrentPanel,
      defaultAccount,
      setDefaultAccount,
      isEmailRequired,
      isFacebookLoginEnabled,
      isGoogleSignUpLoginEnabled,
      isLineLoginEnabled,
      isMobilePhoneRequired,
      isSocialLoginEnabled,
      smsVerificationEnabled,
      emailVerificationEnabled,
      birthdaySetting,
      genderSetting,
      slFeatureService,
      grecaptcha,
      benchatService,
      defaultMembershipPromoBanner,
      isVariantLoading,
      isVariantUI,
      hasThirdpartyLoginUIRevamp,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPanel, defaultAccount, grecaptcha, variant, isVariantLoading],
  );
  return (
    <AppContext.Provider value={{ ...contextProps, ...restProps }}>
      {children}
    </AppContext.Provider>
  );
};

AppContextProvider.propTypes = {
  defaultPanel: PropTypes.string.isRequired,
  children: PropTypes.element.isRequired,
  userSetting: userSettingPropTypes,
  // eslint-disable-next-line react/forbid-prop-types
  featuresLimit: PropTypes.object,
  isFacebookLoginEnabled: PropTypes.bool,
  isGoogleSignUpLoginEnabled: PropTypes.bool,
  isLineLoginEnabled: PropTypes.bool,
  isRecaptchaEnabled: PropTypes.bool,
  recaptchaSiteKey: PropTypes.string,
  defaultMembershipPromoBanner: PropTypes.shape({
    birthday_credits: PropTypes.bool,
    earn_points_from_order: PropTypes.bool,
    membership_tier_discount: PropTypes.bool,
    welcome_credits: PropTypes.number,
  }),
  ruleManagerExternalHost: PropTypes.string,
};
